import { Alert, Button, Spinner } from "reactstrap";
import { useEffect, useState } from "react";
import { BulkDataTransformsRecordCard } from "../../../components/Drawers/BulkDataTransformsDrawer";
import { FeedbackConversation } from "../../../components/BrieChatMenu/BrieChatFeedback";
import useCreateIssue from "../../../api/api-hooks/supportal/useCreateIssue";
import FeedbackTransformDrawer from "./FeedbackTransformDrawer";
import AppDrawer from "../../../components/Drawers/AppDrawer";
import { SPRINTIssue } from "../../../types/GlobalInterfaces";
import AIToolsAPI from "../../../api/api/AIToolsAPI";

interface Props {
  open: boolean;
  records: FeedbackConversation[];
  onClose: () => void;
}

export const TransformFeedbackToIssue = async (
  record: FeedbackConversation
): Promise<SPRINTIssue> => {
  if (!record?.id) throw new Error("Record not found");
  const processResponse = await AIToolsAPI.generateText({
    description: JSON.stringify(record.Messages),
    instructions: `Based on the given user feedback conversation, identify the conversation matter and add brief summary. If there is direct actions that can be taken, create a list of action item(s) for developers/management to resolve the issue`,
  });

  const description = processResponse.data.data.text;

  return {
    id: record.id,
    Description: description || "",
    Type: "Task",
    Status: "Backlog",
    DeveloperNotes: {
      RichText: `<p>Browser Data: ${
        record?.BrowserData || "N/A"
      }</p><p>Last Visited Cloud: ${
        record?.LastVisitedTenant || "N/A"
      }</p><strong>Conversation:</strong> <br> ${
        record?.Messages
          ? record?.Messages.map(
              (message) =>
                `${message?.Sender?.Name || "Unknown User"}: ${message.Message}`
            ).join("<br>")
          : "N/A"
      }</p>`,
      PlainText: `Browser Data: ${
        record?.BrowserData || "N/A"
      }\nLast Visited Cloud: ${record?.LastVisitedTenant || "N/A"}`,
    },
    _RecordName: record._RecordName,
  };
};

function BulkTransformFeedbackDrawer({ open, records, onClose }: Props) {
  const [singleDrawer, setSingleDrawer] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<any>(null);
  const [transformedRecords, setTransformedRecords] = useState<SPRINTIssue[]>(
    []
  );
  const [error, setError] = useState<{
    error: string;
    clear: () => void;
  }>();
  const [warning, setWarning] = useState(false);
  const [loading, setLoading] = useState(true);
  const { createIssues } = useCreateIssue();

  useEffect(() => {
    if (!open) return;
    const fetchTransformedRecords = async () => {
      if (records && records.length > 0) {
        try {
          const promises = records.map((record) =>
            TransformFeedbackToIssue(record)
          );
          const newTransformedRecords = await Promise.all(promises);

          setTransformedRecords(newTransformedRecords);
        } catch (error: Error | any) {
          setError({
            error: `Failed to transform records: ${
              error?.message || "Unknown error"
            }`,
            clear: () => setError(undefined),
          });
        }
      }
    };

    fetchTransformedRecords().then(() => {
      setLoading(false);
      setWarning(true);
    });
  }, [records, open]);

  return (
    <>
      <AppDrawer
        headerOptions={
          <Button onClick={handleConvert} color="success">
            Convert
          </Button>
        }
        open={open}
        onClose={() => {
          onClose();
          setSingleDrawer(false);
          error?.clear();
        }}
        headerTitle="Convert Feedback to Issues"
        body={
          <div className="tw:w-full">
            {!loading && transformedRecords.length > 0 && (
              <>
                {transformedRecords.map((record) => (
                  <BulkDataTransformsRecordCard
                    key={record.id}
                    recordName={record._RecordName}
                    onReview={() => {
                      setSingleDrawer(true);
                      setCurrentRecord(record);
                      setWarning(false);
                    }}
                  />
                ))}
              </>
            )}
            {loading && (
              <div className="tw:w-full tw:flex tw:flex-col  tw:items-center">
                <Spinner color="primary" className="tw:p-5 tw:m-4" />
                <h4>Transforming your Data</h4>
              </div>
            )}
            {error && (
              <Alert
                color="danger"
                isOpen
                toggle={error.clear}
                className="mt-2"
              >
                {error.error}
              </Alert>
            )}
            {warning && (
              <Alert isOpen toggle={() => setWarning(false)} color="warning">
                Review each record before converting
              </Alert>
            )}
          </div>
        }
      />
      <FeedbackTransformDrawer
        open={singleDrawer}
        currentData={currentRecord}
        onSave={(data) => {
          setTransformedRecords((prev) =>
            prev.map((rec) =>
              rec._RecordName === data._RecordName ? data : rec
            )
          );
          setSingleDrawer(false);
        }}
        onClose={() => {
          setSingleDrawer(false);
          setCurrentRecord(null);
        }}
      />
    </>
  );

  function handleConvert() {
    const requiredFields = ["Title", "Type", "Status"];
    const recordsWithMissingFields = transformedRecords.filter((record) => {
      return requiredFields.some((field) => !(record as any)[field]);
    });

    const verifiedRecords = recordsWithMissingFields.length === 0;

    if (!verifiedRecords) {
      const missingFieldsMessage = recordsWithMissingFields
        .map(
          (record, i) =>
            `Record #${i + 1} is missing: ${requiredFields
              .filter((field) => !(record as any)[field])
              .join(", ")}. `
        )
        .join("\n");
      setError({
        error: `Please fill in the required fields for the next records:\n${missingFieldsMessage}`,
        clear: () => setError(undefined),
      });
      return;
    }
    createIssues({
      payload: transformedRecords,
      onSuccess: () => {
        setTransformedRecords([]);
        onClose();
      },
      onError: (error) => {
        setError({
          error: `Failed to create issues: ${
            error?.message || "Unknown error"
          }`,
          clear: () => setError(undefined),
        });
      },
    });
    onClose();
  }
}

export default BulkTransformFeedbackDrawer;
